/**
 * Icone e pictograma — DESIGN_SYSTEM §1.5, §1.6.
 *
 * §1.6: Lucide como **fonte de desenho**, entregue como **sprite SVG externo** — nunca
 * componente React nem fonte de icone. `PWA.md §11.2` reserva **0 KB de JS** para icones, e o
 * sprite e cacheado a parte. Este arquivo nao carrega desenho nenhum: ele so emite o
 * `<use href="sprite#nome">`.
 *
 * §1.6: todo icone e `aria-hidden="true"` por padrao. Icone sozinho em botao exige
 * `aria-label` no BOTAO (verificado por lint e pelo tipo de `IconButton`), nao no icone —
 * rotular o SVG e rotular o desenho, nao a acao.
 */

import type { ReactNode } from 'react';

import { cx } from './util';

/** §1.6 — tamanhos do sistema. 16 inline, 20 em botao/campo, 24 navegacao, 32/48 estado vazio. */
export type TamanhoDeIcone = 16 | 20 | 24 | 32 | 48;

/**
 * §1.5 — os 8 pictogramas proprietarios de categoria, mais os de estado usados por
 * `EmptyState`/`ErrorState`. O union fechado e o que impede um `EmptyState` sem pictograma
 * decidido: `PictogramName` errado nao compila.
 */
export type PictogramName =
  | 'medicamento'
  | 'vitamina'
  | 'higiene'
  | 'beleza'
  | 'bebe'
  | 'perfumaria'
  | 'correlato'
  | 'alimento'
  | 'busca-sem-resultado'
  | 'carrinho-vazio'
  | 'sem-pedidos'
  | 'sem-receitas'
  | 'sem-cupons'
  | 'offline'
  | 'loja-fechada'
  | 'erro';

export type IconName = string;

/** Caminho do sprite. Configuravel porque o app decide onde o asset e servido. */
export const SPRITE_PADRAO = '/icons/balcao.svg';
export const SPRITE_PICTOGRAMAS_PADRAO = '/icons/pictogramas.svg';

export interface IconProps {
  readonly name: IconName;
  readonly size?: TamanhoDeIcone;
  readonly className?: string;
  readonly sprite?: string;
  /**
   * So use quando o icone carrega informacao que nenhum texto vizinho carrega. Na duvida,
   * deixe `aria-hidden` (o padrao) e escreva o texto (§3.8: "todo icone informativo tem texto
   * equivalente; todo icone decorativo e aria-hidden").
   */
  readonly label?: string;
}

export function Icon({ name, size = 20, className, sprite = SPRITE_PADRAO, label }: IconProps): ReactNode {
  const decorativo = label === undefined;
  return (
    <svg
      width={size}
      height={size}
      className={cx('balcao-icon', className)}
      aria-hidden={decorativo ? true : undefined}
      role={decorativo ? undefined : 'img'}
      aria-label={label}
      focusable="false"
    >
      <use href={`${sprite}#${name}`} />
    </svg>
  );
}

export interface PictogramProps {
  readonly name: PictogramName;
  readonly size?: 32 | 48;
  readonly sprite?: string;
  readonly className?: string;
}

/**
 * §1.5 — pictograma de categoria como placeholder de foto ausente.
 *
 * "Nunca icone de 'imagem quebrada', nunca icone de camera, nunca texto 'sem imagem'."
 * Alternativa descartada: placeholder cinza generico — com > 40 % do catalogo sem foto no ano
 * 1, o cinza faz a grade inteira parecer quebrada; o pictograma parece intencional.
 */
export function Pictogram({
  name,
  size = 48,
  sprite = SPRITE_PICTOGRAMAS_PADRAO,
  className,
}: PictogramProps): ReactNode {
  return (
    <svg width={size} height={size} className={cx('balcao-pictogram', className)} aria-hidden focusable="false">
      <use href={`${sprite}#${name}`} />
    </svg>
  );
}
